import React, { useEffect, useMemo, useState } from "react";
import { BookingModeFields } from "@/components/BookingModeFields";
import { SeatingFields } from "@/components/SeatingFields";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { PageTitle } from "@/components/SettingsPanel";
import { Input, Label } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { imgSrc } from "@/lib/img";
import { TZ } from "@/lib/time";
import { DateTimeField } from "@/components/DateField";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { EventType, Venue } from "@/types";

const toLocal = (iso: string) => new Date(iso).toLocaleString("sv-SE", { timeZone: TZ }).slice(0, 16).replace(" ", "T");
const toIso = (local: string) => `${local}:00+03:00`;
// Copies keep the same weekday/time and length, moved forward by whole weeks until they are in the future
const nextWeekly = (startIso: string, endIso: string) => {
  const week = 7 * 864e5, len = new Date(endIso).getTime() - new Date(startIso).getTime();
  let s = new Date(startIso).getTime();
  do s += week; while (s <= Date.now());
  return { start: toLocal(new Date(s).toISOString()), end: toLocal(new Date(s + len).toISOString()), shift: s - new Date(startIso).getTime() };
};
const box = "rounded-3xl border bg-card p-5 space-y-4";
const sel = "h-11 w-full rounded-2xl border bg-background px-3 text-sm";

const blank = { booking_mode: "open", repeat_days: 1, skip_weekdays: [] as number[], venue_id: "", type: "", title_ar: "", title_en: "", poster: "", short_ar: "", short_en: "", details_ar: "", start: "", end: "",
  pricing_model: "paid", price: { amount: "", currency: "IQD" }, seating_types: [] as string[], vip_fee: { amount: "", currency: "IQD" }, vip_fee_per: "person", price_includes: "", charged_separately: "",
  requirements: "", terms: "", maps_url: "", address: "", confirmation_minutes: 120, grace_minutes: 30, cancel_cutoff_minutes: "" as any, close: "" };

const afterDays = (start: string, end: string, days: number) => {
  const shift = days * 864e5;
  return { shift, start: toLocal(new Date(new Date(start).getTime() + shift).toISOString()), end: toLocal(new Date(new Date(end).getTime() + shift).toISOString()) };
};

export default function EventForm() {
  const { id } = useParams();
  const [qs] = useSearchParams();
  const copyId = qs.get("copy");
  const extend = Number(qs.get("extend")) || 0;
  const [copiedFrom, setCopiedFrom] = useState("");
  const { t, lang } = usePrefs();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [f, setF] = useState<any>(blank);
  const [types, setTypes] = useState<EventType[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [venuesLoaded, setVenuesLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }));
  // Moving the start moves the end too, keeping the event length
  const moveStart = (v: string) => setF((p: any) => {
    const ms = (x: string) => new Date(toIso(x)).getTime();
    if (!p.start || !p.end || !v || v.length < 16 || p.start.length < 16) return { ...p, start: v };
    const d = ms(v) - ms(p.start);
    return { ...p, start: v, end: toLocal(new Date(ms(p.end) + d).toISOString()), close: p.close?.length === 16 ? toLocal(new Date(ms(p.close) + d).toISOString()) : p.close };
  });

  useEffect(() => {
    api.get("/event-types").then((r) => setTypes(r.data));
    api.get("/venue/venues").then((r) => { setVenues(r.data.filter((v: Venue) => (v.review_status || "approved") === "approved" && v.events_enabled)); setVenuesLoaded(true); });
    const src = id || copyId;
    if (src) api.get("/venue/events").then((r) => {
      const e = r.data.find((x: any) => x.event_id === src);
      if (!e) return;
      // Older events may use meal models or options: map them onto free/paid with the lowest option price
      const { options, programs, ...rest } = e;
      const isPaid = e.pricing_model.startsWith("paid");
      const wk = id ? null : extend ? afterDays(e.start_at, e.end_at, 1) : nextWeekly(e.start_at, e.end_at);
      const close = e.requests_close_at ? toLocal(wk ? new Date(new Date(e.requests_close_at).getTime() + wk.shift).toISOString() : e.requests_close_at) : "";
      setF({ ...blank, ...rest, start: toLocal(e.start_at), end: toLocal(e.end_at), pricing_model: isPaid ? "paid" : "free",
        price: e.price || (isPaid && (e.price_from?.[0] || options?.[0]?.price)) || blank.price, cancel_cutoff_minutes: e.cancel_cutoff_minutes ?? "", close, booking_mode: e.booking_mode || "open", repeat_days: extend ? Math.min(30, extend) : 1, skip_weekdays: [], seating_types: e.seating_types || [], vip_fee: e.vip_fee || blank.vip_fee, vip_fee_per: e.vip_fee_per || "person",
        ...(wk ? { event_id: undefined, start: wk.start, end: wk.end } : {}) });
      if (!id) setCopiedFrom(lang === "ar" ? e.title_ar : e.title_en || e.title_ar);
    });
  }, [id, copyId, extend]); // eslint-disable-line react-hooks/exhaustive-deps

  const type = useMemo(() => types.find((x) => x.slug === f.type), [types, f.type]);
  const paid = f.pricing_model === "paid";

  const uploadPoster = async (file?: File) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/venue/uploads", fd);
      set("poster", data.url);
    } catch (e) {
      toast.error(errorText(e, t));
    }
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!f.start || !f.end) return toast.error(t("dp.required"));
    setBusy(true);
    const { start, end, close, event_id, repeat_days, skip_weekdays, ...rest } = f;
    const body = { ...rest, start_at: toIso(start), end_at: toIso(end), requests_close_at: close?.length === 16 ? toIso(close) : null, price: paid ? f.price : null, options: [], programs: [],
      vip_fee: f.seating_types.includes("vip") && f.vip_fee.amount ? { amount: f.vip_fee.amount, currency: paid ? f.price.currency : f.vip_fee.currency } : null,
      cancel_cutoff_minutes: f.cancel_cutoff_minutes === "" ? null : Number(f.cancel_cutoff_minutes), ...(id ? {} : { repeat_days: Math.min(30, Math.max(1, Number(repeat_days) || 1)), skip_weekdays }) };
    try {
      if (id) await api.put(`/venue/events/${id}`, body);
      else {
        const { data } = await api.post("/venue/events", body);
        if (data.series_count > 1) {
          toast.success(t("ve.seriesSaved").replace("{n}", String(data.series_count)));
          return navigate("/venue/events");
        }
        const { data: all } = await api.get("/venue/events");
        const published = all.filter((x: any) => x.status === "published");
        if (data.status === "published" && published.length === 1 && published[0].event_id === data.event_id) {
          toast.success(t("ve.saved"));
          return navigate("/venue/events", { state: { celebrate: data } });
        }
      }
      toast.success(t("ve.saved"));
      navigate("/venue/events");
    } catch (e) {
      toast.error(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };

  const txt = (k: string, label: string, props: any = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`ef-${k}`}>{t(label)}</Label>
      <Input id={`ef-${k}`} value={f[k] ?? ""} onChange={(e: any) => set(k, props.type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`event-${k}-input`} className="h-11 rounded-2xl" {...props} />
    </div>
  );

  if (user && user.venue_role !== "owner") return <Navigate to="/venue/events" replace />;
  return (
    <form onSubmit={submit} className="space-y-5 max-w-3xl" data-testid="event-form">
      <PageTitle title={t(id ? "ve.edit" : copiedFrom ? "ve.copyTitle" : "ve.new")} />
      {copiedFrom && (
        <div data-testid="event-copy-banner" className="rounded-2xl border border-primary/30 bg-primary/5 p-4 flex gap-3 text-sm">
          <Copy className="h-5 w-5 text-primary shrink-0" />
          <p><b>{t(extend ? "ve.extendFrom" : "ve.copyFrom").replace("{title}", copiedFrom)}</b><br /><span className="text-muted-foreground">{t(extend ? "ve.extendHint" : "ve.copyHint")}</span></p>
        </div>
      )}
      <div className={box}>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>{t("ve.f.venue")}</Label>
            <select required value={f.venue_id} onChange={(e) => set("venue_id", e.target.value)} data-testid="event-venue-select" className={sel}>
              <option value="" disabled>—</option>{venues.map((v) => <option key={v.venue_id} value={v.venue_id}>{lang === "ar" ? v.name_ar : v.name_en || v.name_ar}</option>)}
            </select>
            {venuesLoaded && !venues.length && <p data-testid="event-no-approved-venues" className="text-xs text-amber-700 dark:text-amber-400">{t("vr.noApproved")}</p>}</div>
          <div className="space-y-1.5"><Label>{t("ve.f.type")}</Label>
            <select required value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="event-type-select" className={sel}>
              <option value="" disabled>—</option>{types.map((x) => <option key={x.slug} value={x.slug}>{lang === "ar" ? x.name_ar : x.name_en}</option>)}
            </select></div>
          {txt("title_ar", "ve.f.titleAr", { required: true, minLength: 2 })}
          {txt("title_en", "ve.f.titleEn", { dir: "ltr" })}
          {txt("short_ar", "ve.f.shortAr", { required: true, minLength: 2 })}
          {(["start", "end"] as const).map((k) => (
            <div key={k} className="space-y-1.5"><Label>{t(`ve.f.${k}`)}</Label>
              <DateTimeField value={f[k] || ""} onChange={(v) => (k === "start" ? moveStart(v) : set(k, v))} min={k === "end" ? f.start || undefined : undefined} testId={`event-${k}-input`} /></div>
          ))}
          <div className="space-y-1.5"><Label>{t("ve.f.poster")}</Label>
            <div className="flex items-center gap-2">{f.poster && <img src={imgSrc(f.poster)} alt="" className="h-11 w-11 rounded-xl object-cover" />}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" onChange={(e) => uploadPoster(e.target.files?.[0])} data-testid="event-poster-input" className="text-xs" /></div></div>
        </div>
        <div className="space-y-1.5"><Label>{t("ve.f.details")}</Label>
          <textarea rows={3} value={f.details_ar} onChange={(e) => set("details_ar", e.target.value)} data-testid="event-details-input" className="w-full rounded-2xl border bg-background p-3 text-sm" /></div>
      </div>
      {type && (
        <div className={box} data-testid="event-pricing-section">
          <div className="space-y-1.5"><Label>{t("ve.f.pricing")}</Label>
            <select value={f.pricing_model} onChange={(e) => set("pricing_model", e.target.value)} data-testid="event-pricing-select" className={sel}>
              {["free", "paid"].map((m) => <option key={m} value={m}>{t(`ev.pricing.${m}`)}</option>)}
            </select></div>
          {paid && (
            <div className="flex gap-2 items-end">
              <div className="flex-1">{txt("_", "ve.f.price", { value: f.price.amount, onChange: (e: any) => set("price", { ...f.price, amount: e.target.value }), dir: "ltr", required: true, "data-testid": "event-price-input" })}</div>
              <select value={f.price.currency} onChange={(e) => set("price", { ...f.price, currency: e.target.value })} data-testid="event-currency-select" className="h-11 rounded-2xl border bg-background px-3 text-sm font-bold"><option>IQD</option><option>USD</option></select>
            </div>
          )}
          <SeatingFields f={f} set={set} />
          <div className="grid sm:grid-cols-2 gap-3">
            {txt("price_includes", "ev.includes")}
            {txt("charged_separately", "ev.separately")}
            {txt("requirements", "ev.requirements")}
            {txt("terms", "ev.terms")}
          </div>
        </div>
      )}
      <BookingModeFields f={f} set={set} isNew={!id} />
      <div className={`${box} grid sm:grid-cols-2 gap-3`}>
        {txt("confirmation_minutes", "ve.f.confirm", { type: "number", min: 30, max: 120 })}
        {txt("grace_minutes", "ve.f.grace", { type: "number", min: 0 })}
        <div className="space-y-1.5 sm:col-span-2" data-testid="event-close-section">
          <Label>{t("ve.f.autoClose")}</Label>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-0"><DateTimeField value={f.close || ""} onChange={(v) => set("close", v)} min={toLocal(new Date().toISOString())}
              max={f.end?.length === 16 ? toLocal(new Date(new Date(toIso(f.end)).getTime() - 6e4).toISOString()) : undefined} testId="event-close-input" /></div>
            {f.close && <button type="button" onClick={() => set("close", "")} data-testid="event-close-clear" className="h-10 px-4 rounded-full bg-accent text-xs font-bold">{t("ve.f.autoCloseClear")}</button>}
          </div>
          <p className="text-xs text-muted-foreground">{t("ve.f.autoCloseHint")}</p>
        </div>
        {txt("cancel_cutoff_minutes", "ve.f.cancelCut", { type: "number", min: 0 })}
        {txt("maps_url", "ve.f.maps", { dir: "ltr" })}
        {txt("address", "ve.f.address")}
      </div>
      <button disabled={busy} data-testid="event-save-button" className="w-full sm:w-auto h-12 px-10 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-60">{busy ? t("common.loading") : t("common.save")}</button>
    </form>
  );
}
